import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Sparkles } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * AlertTicker — fixed-height status bar that shows active monitor alerts.
 *
 * Two visual styles:
 *   - 'classic': single 34px row, latest on the left + scrolling list on the right
 *   - 'cnn':     two-row breaking-news layout (BREAKING headline + sub-ticker)
 * The user toggles between them via the sparkle button; preference is per-browser
 * in localStorage so the choice survives reloads but doesn't get pushed to other
 * devices unless we wire it into user settings later.
 *
 * Sources merged: monitor alerts (assets/probes down) + backup_monitor alerts
 * (failing backup jobs). Both scoped server-side: internal callers by
 * companyScope, portal callers by req.user.company_id. Acknowledged alerts and
 * non-critical severities are excluded — see the for_ticker=1 filter on each
 * route. Dismissable per-session (sessionStorage) so a real outage can't get
 * permanently silenced.
 */

const STYLE_STORAGE_KEY = 'alertTickerStyle';
const VALID_STYLES = new Set(['classic', 'cnn']);

export default function AlertTicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, hasModule } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('alertTickerDismissed') === '1'; }
    catch { return false; }
  });
  const [tickerStyle, setTickerStyle] = useState(() => {
    try {
      const v = localStorage.getItem(STYLE_STORAGE_KEY);
      return VALID_STYLES.has(v) ? v : 'classic';
    } catch { return 'classic'; }
  });

  // CNN-style headline rotates through all active alerts every ~6s, like a real
  // news lower-third. Latest is always alerts[0] so it leads. Hover pauses.
  const [headlineIdx, setHeadlineIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // Live clock shown in the top-right of the CNN headline. Updates every second.
  const [now, setNow] = useState(() => new Date());

  const isPortal = user?.role === 'client';

  // The ticker renders for EVERY user, but /backup-monitor is requireModule-gated.
  // Asking anyway meant a 403 every 30s, forever, for every agent without the module
  // — 6.324 of them for one agent in two weeks. The .catch() below hid it from the UI
  // but not from the server, the logs, or the friction telemetry (where it drowned out
  // every real signal). Don't call what you're not allowed to call.
  const canSeeBackups = hasModule('backup_monitor');

  const fetchAlerts = useCallback(async () => {
    const empty = Promise.resolve({ data: [] });
    const requests = isPortal
      ? [
          api.get('/portal/monitor/alerts').catch(() => ({ data: { alerts: [] } })),
          canSeeBackups
            ? api.get('/portal/backup-monitor/alerts').catch(() => ({ data: { alerts: [] } }))
            : empty,
        ]
      : [
          api.get('/monitor/alerts?status=active&unacknowledged=1&for_ticker=1&limit=30')
            .catch(() => ({ data: { alerts: [] } })),
          canSeeBackups
            ? api.get('/backup-monitor/alerts?for_ticker=1').catch(() => ({ data: [] }))
            : empty,
        ];

    try {
      const [monitorRes, backupRes] = await Promise.all(requests);
      const monitorAlerts = (monitorRes.data?.alerts || []).map(a => ({
        ...a,
        _source: 'monitor',
      }));
      // /backup-monitor/alerts returns a bare array; /portal/backup-monitor/alerts wraps it.
      const backupRaw = isPortal ? (backupRes.data?.alerts || []) : (backupRes.data || []);
      const backupAlerts = backupRaw.map(a => ({
        ...a,
        _source: 'backup',
        asset_name: a.job_name || a.asset_name,
      }));
      const merged = [...monitorAlerts, ...backupAlerts]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAlerts(merged);
    } catch {
      setAlerts([]);
    }
  }, [isPortal, canSeeBackups]);

  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, 30000);
    return () => clearInterval(id);
  }, [fetchAlerts]);

  // Reset headline rotation when alerts change so the freshest one shows first.
  useEffect(() => { setHeadlineIdx(0); }, [alerts.length, alerts[0]?.id]);

  // Headline rotation tick (CNN style only, paused on hover or single-alert).
  useEffect(() => {
    if (tickerStyle !== 'cnn' || paused || alerts.length <= 1) return;
    const id = setInterval(() => {
      setHeadlineIdx(i => (i + 1) % alerts.length);
    }, 6000);
    return () => clearInterval(id);
  }, [tickerStyle, paused, alerts.length]);

  // Live clock (CNN only — saves a tick when the bar isn't visible)
  useEffect(() => {
    if (tickerStyle !== 'cnn') return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [tickerStyle]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem('alertTickerDismissed', '1'); } catch {}
    setDismissed(true);
  };

  const handleReopen = (e) => {
    e.stopPropagation();
    try { sessionStorage.removeItem('alertTickerDismissed'); } catch {}
    setDismissed(false);
  };

  const handleToggleStyle = (e) => {
    e.stopPropagation();
    const next = tickerStyle === 'classic' ? 'cnn' : 'classic';
    setTickerStyle(next);
    try { localStorage.setItem(STYLE_STORAGE_KEY, next); } catch {}
  };

  const goToAlert = (alert) => {
    if (alert?._source === 'backup') {
      navigate(isPortal ? '/portal/backups' : '/backup-monitor');
      return;
    }
    if (alert?.asset_id) {
      const path = isPortal
        ? `/portal/monitors/${alert.asset_id}`
        : `/monitor/${alert.asset_id}`;
      navigate(path);
    } else {
      navigate(isPortal ? '/portal/monitors' : '/monitor');
    }
  };

  const latest = alerts[0];

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return t('alertTicker.justNow');
    if (diff < 3600) return t('alertTicker.minutesAgo', { n: Math.floor(diff / 60) });
    if (diff < 86400) return t('alertTicker.hoursAgo', { n: Math.floor(diff / 3600) });
    return t('alertTicker.daysAgo', { n: Math.floor(diff / 86400) });
  };

  const labelFor = (alert) => {
    if (!alert) return '';
    const parts = [];
    if (!isPortal && alert.company_name) parts.push(alert.company_name);
    if (alert.location_name) parts.push(alert.location_name);
    if (alert.asset_name) parts.push(alert.asset_name);
    const where = parts.length ? parts.join(' · ') : t('alertTicker.unknownAsset');
    const what = alert.title || alert.message || alert.alert_type || '';
    return what ? `${where} — ${what}` : where;
  };

  // "Where" only — used in the CNN sub-ticker where space is at a premium and
  // the headline already shows the "what".
  const labelWhereOnly = (alert) => {
    if (!alert) return '';
    const parts = [];
    if (!isPortal && alert.company_name) parts.push(alert.company_name);
    if (alert.location_name) parts.push(alert.location_name);
    if (alert.asset_name) parts.push(alert.asset_name);
    return parts.length ? parts.join(' · ') : t('alertTicker.unknownAsset');
  };

  const dotColor = (sev) => {
    if (sev === 'warning') return '#f59e0b';
    if (sev === 'info') return '#facc15';
    return '#ef4444';
  };

  // A "degraded" monitor alert = a device that's down but the site stays online
  // via link redundancy. The worker tags these severity='warning' + alert_type
  // 'status_critical'. We surface them in the ticker (Andy's call) but in amber,
  // not red, so a full outage still reads as the more urgent event.
  const isDegraded = (a) =>
    a && a._source !== 'backup' && a.severity === 'warning';

  // Accent color for a row/bullet: backups + degraded links = amber, real
  // outages = red. Keeps the visual hierarchy honest in the CNN layout (where
  // colors were previously hardcoded red for every monitor alert).
  const accentColor = (a) => {
    if (!a) return '#ef4444';
    if (a._source === 'backup') return '#f59e0b';
    return dotColor(a.severity);
  };

  const scrollItems = useMemo(() => [...alerts, ...alerts], [alerts]);

  if (!alerts.length) return null;

  // Dismissed but alerts still active → render a thin "tirita" in the same slot
  // the bar would occupy. Default state is a 7px solid red strip with a white
  // shimmer sweeping across it (radar feel). Hover/focus expands it to 28px and
  // reveals the counter — gives both a minimal resting state and an obvious
  // "click here" affordance. Lives in normal flow so main content shifts down
  // by exactly the strip's height; no fixed positioning, no header overlap.
  if (dismissed) {
    return (
      <button
        type="button"
        className="alert-ticker-strip"
        onClick={handleReopen}
        title={t('alertTicker.reopenWithCount', { n: alerts.length })}
        aria-label={t('alertTicker.reopenWithCount', { n: alerts.length })}
      >
        <span className="alert-ticker-strip-shimmer" aria-hidden="true" />
        <span className="alert-ticker-strip-content">
          <span className="alert-ticker-strip-dot" />
          {t('alertTicker.reopenWithCount', { n: alerts.length })}
        </span>
      </button>
    );
  }

  // ── Toolbar (toggle + dismiss) ─────────────────────────────────────────
  const toolbar = (
    <div className="alert-ticker-tools">
      <button
        type="button"
        className="alert-ticker-toolbtn"
        onClick={handleToggleStyle}
        title={tickerStyle === 'classic'
          ? t('alertTicker.switchToCnn')
          : t('alertTicker.switchToClassic')}
        aria-label={t('alertTicker.toggleStyle')}
      >
        <Sparkles size={13} />
      </button>
      <button
        type="button"
        className="alert-ticker-toolbtn"
        onClick={handleDismiss}
        title={t('alertTicker.dismiss')}
        aria-label={t('alertTicker.dismiss')}
      >
        <X size={14} />
      </button>
    </div>
  );

  // ── CNN-style breaking news layout ────────────────────────────────────
  if (tickerStyle === 'cnn') {
    const headline = alerts[headlineIdx] || latest;
    const headlineDegraded = isDegraded(headline);
    const sourceLabel = headline?._source === 'backup'
      ? t('alertTicker.sourceBackup')
      : headlineDegraded
        ? t('alertTicker.sourceDegraded')
        : t('alertTicker.sourceOutage');
    const sourceColor = accentColor(headline);
    const clockHHMMSS = now.toLocaleTimeString('es-AR', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });

    return (
      <div
        className="alert-ticker-cnn"
        role="status"
        aria-live="polite"
        aria-label={t('alertTicker.ariaLabel')}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Top row: BREAKING + source pill + rotating headline + live clock */}
        <button
          type="button"
          className="alert-ticker-cnn-headline"
          onClick={() => goToAlert(headline)}
          title={labelFor(headline)}
        >
          <span className="alert-ticker-cnn-breaking">
            <span className="alert-ticker-cnn-live-dot" />
            {t('alertTicker.breaking')}
          </span>
          <span
            className="alert-ticker-cnn-source"
            style={{ background: sourceColor }}
          >
            {sourceLabel}
          </span>
          {/* key={...} forces React to remount the text on rotation, which
              re-triggers the slide-in animation via CSS */}
          <span
            key={`${headline?.id}-${headlineIdx}`}
            className="alert-ticker-cnn-headline-text"
          >
            {labelFor(headline)}
          </span>
          <span className="alert-ticker-cnn-meta">
            <span className="alert-ticker-cnn-time">
              {timeAgo(headline?.created_at)}
            </span>
            <span className="alert-ticker-cnn-clock">
              <span className="alert-ticker-cnn-clock-dot" />
              {clockHHMMSS}
            </span>
          </span>
        </button>

        {/* Bottom row: ALERTAS pill + scrolling sub-ticker */}
        <div className="alert-ticker-cnn-subbar">
          <span className="alert-ticker-cnn-category">
            {t('alertTicker.activeAlerts')}
            <span className="alert-ticker-cnn-category-count">{alerts.length}</span>
          </span>
          <div className="alert-ticker-cnn-scroll-wrap">
            <div
              className="alert-ticker-cnn-scroll"
              style={paused ? { animationPlayState: 'paused' } : undefined}
            >
              {scrollItems.map((a, idx) => (
                <button
                  type="button"
                  key={`${a.id}-${idx}`}
                  className="alert-ticker-cnn-item"
                  onClick={() => goToAlert(a)}
                  title={labelFor(a)}
                >
                  <span
                    className="alert-ticker-cnn-bullet"
                    style={{ background: accentColor(a) }}
                  />
                  <span className="alert-ticker-cnn-item-where">
                    {labelWhereOnly(a)}
                  </span>
                  <span className="alert-ticker-cnn-sep">›</span>
                </button>
              ))}
            </div>
          </div>
          {/* Pagination dots — show position within the rotation set, only
              meaningful when there are 2+ alerts */}
          {alerts.length > 1 && (
            <span
              className="alert-ticker-cnn-dots"
              aria-label={t('alertTicker.headlinePosition', {
                n: headlineIdx + 1, total: alerts.length,
              })}
            >
              {alerts.slice(0, 8).map((_, i) => (
                <span
                  key={i}
                  className={`alert-ticker-cnn-dot${i === headlineIdx ? ' alert-ticker-cnn-dot-active' : ''}`}
                />
              ))}
              {alerts.length > 8 && <span className="alert-ticker-cnn-dot-more">…</span>}
            </span>
          )}
        </div>

        {toolbar}
      </div>
    );
  }

  // ── Classic single-row layout ─────────────────────────────────────────
  return (
    <div
      className="alert-ticker"
      role="status"
      aria-live="polite"
      aria-label={t('alertTicker.ariaLabel')}
    >
      <button
        type="button"
        className="alert-ticker-latest"
        onClick={() => goToAlert(latest)}
        title={labelFor(latest)}
      >
        <span
          className="alert-ticker-dot alert-ticker-dot-pulse"
          style={{ background: dotColor(latest?.severity) }}
        />
        <span className="alert-ticker-latest-label">
          {t('alertTicker.latest')}:
        </span>
        <span className="alert-ticker-latest-text">
          {labelFor(latest)}
        </span>
        <span className="alert-ticker-latest-time">
          {timeAgo(latest?.created_at)}
        </span>
      </button>

      <div className="alert-ticker-scroll-wrap">
        <div className="alert-ticker-scroll">
          {scrollItems.map((a, idx) => (
            <button
              type="button"
              key={`${a.id}-${idx}`}
              className="alert-ticker-item"
              onClick={() => goToAlert(a)}
              title={labelFor(a)}
            >
              <span
                className="alert-ticker-dot"
                style={{ background: dotColor(a.severity) }}
              />
              <span className="alert-ticker-item-text">
                {labelFor(a)}
              </span>
              <span className="alert-ticker-sep">·</span>
            </button>
          ))}
        </div>
      </div>

      {toolbar}
    </div>
  );
}
